import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Auth, authState, idToken } from '@angular/fire/auth';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CLIENT_PLATFORM_HEADER, CLIENT_PLATFORM } from '../shared/client-platform';
import { AppCheckService } from './app-check.service';
import { UserService } from '../user/user.service';

interface SessionInfoResponse {
  success?: boolean;
  role: 'admin' | 'provider' | null;
  idle_minutes: number | null;
  absolute_hours: number | null;
  idle_deadline_at: number | null;
  absolute_deadline_at: number | null;
  server_time: number;
}

const WARNING_LEAD_MS = 60 * 1000;

/** What the in-app dialog shows: the moment the session ends, or null when no warning is due. */
export interface SessionWarning {
  deadline: number;
}

/** What a deadline refresh learned: extended, the session is gone, or the server was unreachable. */
export type RefreshResult = 'ok' | 'gone' | 'unreachable';

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private http = inject(HttpClient);
  private auth = inject(Auth);
  private router = inject(Router);
  private appCheckService = inject(AppCheckService);
  private userService = inject(UserService);

  private authSub: Subscription | null = null;
  // One timer that fires at min(idle_deadline_at, absolute_deadline_at).
  // The middleware-side checks both caps and revokes on the earlier one;
  // we mirror that here so the client and server agree on a single moment.
  private deadlineTimer: ReturnType<typeof setTimeout> | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private warningShown = false;
  private currentDeadline = 0;
  /** The dialog subscribes here; the service never touches the DOM. */
  private readonly warningSubject = new BehaviorSubject<SessionWarning | null>(null);
  readonly warning$ = this.warningSubject.asObservable();

  start(): void {
    if (this.authSub) return;
    this.authSub = authState(this.auth).subscribe(async (user) => {
      if (user) {
        await this.refreshDeadlines();
      } else {
        this.clearTimers();
      }
    });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.clearTimers();
  }

  /** Re-reads the server's deadlines: 'ok' re-armed the timers, 'gone' cleared them because there is no session, 'unreachable' changed nothing. */
  async refreshDeadlines(): Promise<RefreshResult> {
    let info: SessionInfoResponse | null = null;
    try {
      const token = await firstValueFrom(idToken(this.auth));
      if (!token) return 'gone';
      const appCheckToken = await this.appCheckService.getAppCheckToken();
      let headers = new HttpHeaders({ Authorization: `Bearer ${token}`, [CLIENT_PLATFORM_HEADER]: CLIENT_PLATFORM });
      if (appCheckToken) headers = headers.set('X-Firebase-AppCheck', appCheckToken);
      info = await firstValueFrom(
        this.http.get<SessionInfoResponse>(`${environment.baseURL}/v1/auth/session-info`, { headers })
      );
    } catch {
      // The HTTP interceptor handles 401/403; for any other failure, leave any
      // previously-armed timers (and any warning) in place so we still proactively redirect.
      return 'unreachable';
    }

    if (!info || !info.role) {
      this.clearTimers();
      return 'gone';
    }

    const skew = Date.now() - info.server_time;
    this.scheduleTimers(info, skew);
    return 'ok';
  }

  private scheduleTimers(info: SessionInfoResponse, clientSkewMs: number): void {
    this.clearTimers();
    this.warningShown = false;

    const candidates: number[] = [];
    if (info.idle_deadline_at != null) candidates.push(info.idle_deadline_at + clientSkewMs);
    if (info.absolute_deadline_at != null) candidates.push(info.absolute_deadline_at + clientSkewMs);
    if (!candidates.length) return;

    const deadline = Math.min(...candidates);
    this.currentDeadline = deadline;
    const msUntilDeadline = deadline - Date.now();
    if (msUntilDeadline <= 0) {
      void this.forceSignOut();
      return;
    }

    const msUntilWarning = msUntilDeadline - WARNING_LEAD_MS;
    if (msUntilWarning > 0) {
      this.warningTimer = setTimeout(() => this.showWarning(), msUntilWarning);
    } else {
      this.showWarning();
    }

    this.deadlineTimer = setTimeout(() => this.forceSignOut(), msUntilDeadline);
  }

  private showWarning(): void {
    if (this.warningShown) return;
    this.warningShown = true;
    // The in-app dialog (SessionTimeoutDialogComponent) shows a countdown to
    // this deadline. "Stay signed in" calls keepAlive(): any authenticated
    // request bumps last_activity_at server-side and re-reads the deadlines,
    // which reschedules the timers and clears the warning. If nothing happens,
    // the deadlineTimer fires the forced sign-out below.
    this.warningSubject.next({ deadline: this.currentDeadline });
  }

  /**
   * Extend the session: one authenticated request refreshes the server clock and the timers.
   * Rejects when the server could not be reached, so the dialog can say so and offer a retry.
   */
  async keepAlive(): Promise<void> {
    const result = await this.refreshDeadlines();
    if (result === 'unreachable') throw new Error('session could not be extended');
    // The server says there is no session to extend: leave, do not pretend it was extended.
    if (result === 'gone') await this.forceSignOut();
  }

  /** The user chose to leave now rather than wait for the deadline. */
  async signOutNow(): Promise<void> {
    await this.forceSignOut();
  }

  private clearTimers(): void {
    if (this.deadlineTimer) clearTimeout(this.deadlineTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    this.deadlineTimer = null;
    this.warningTimer = null;
    this.warningShown = false;
    this.currentDeadline = 0;
    if (this.warningSubject.value) this.warningSubject.next(null);
  }

  private async forceSignOut(): Promise<void> {
    this.clearTimers();
    const currentUrl = this.router.url;
    await this.userService.forceTimeoutSignOut(currentUrl);
  }
}
